import React, { Fragment } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { useQuery } from 'react-apollo';

import SignupAlbumLayout from '../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import PinVerificationStep from './components/PinVerificationStep';
import useMarketingAccountOnboarding from './hooks/useMarketingAccountOnboarding';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { AlbumFolderForMarketingAccounts } from '../../../graphql/queries/album-folder';
import { getAsset } from '../../../util/assets';
import { getErrorString } from '../../../util/normalize-errors';

const MarketingAccountOnboardingView = () => {
  const location = useLocation();
  const history = useHistory();
  const openAlert = useOpenAlert();
  const {
    firstName,
    emailAddress,
    albumFolderId,
    accountId: marketingAccountId,
  } = qs.parse(location.search ?? '');

  const {
    loading: onboardingLoading,
    onPinVerify,
    onPinCodeResend,
    onMarketingAccountLink,
  } = useMarketingAccountOnboarding({ emailAddress });

  const { data: { albumFolder } = {}, loading: queryLoading } = useQuery(
    AlbumFolderForMarketingAccounts,
    {
      variables: { id: albumFolderId },
      skip: !albumFolderId,
    },
  );

  const orgLogo = albumFolder?.account?.logo_image?.url;

  const onSignIn = async () => {
    try {
      if (marketingAccountId) {
        await onMarketingAccountLink(marketingAccountId);
      }

      history.push(`/discover/album-folder/${albumFolderId}`);
    } catch (e) {
      openAlert({
        message: 'There was an error linking identities',
        info: getErrorString(e),
      });
    }
  };

  const onSubmit = async values => {
    try {
      const result = await onPinVerify(values);
      if (result?.data?.accountLoginByEmail?.success) {
        await onSignIn();
      }
    } catch (e) {
      openAlert({
        message: 'There was an error linking identities',
        info: getErrorString(e),
      });
    }
  };

  if (queryLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Verify Email</title>
      </Helmet>
      <div className="marketing-account-onboarding-wrapper">
        <div className="d-flex justify-content-between align-items-center p-3">
          <div className="org-logo">
            {orgLogo && (
              <img
                src={orgLogo}
                alt="Organization Logo"
                style={{ height: '40px' }}
              />
            )}
          </div>
          <div className="waldo-logo">
            <img
              src={getAsset('/powered-by-waldo-blue.svg')}
              alt="Waldo"
              style={{ height: '30px' }}
            />
          </div>
        </div>

        <SignupAlbumLayout.Wrapper
          imageDisabled
          logoSrc={null}
          withCTA={false}
          className="marketing-account-onboarding-layout"
        >
          <div className="marketing-account-onboarding-content-container">
            <Formik
              onSubmit={onSubmit}
              initialValues={{ code: '' }}
              render={({ values, handleSubmit, handleChange }) => (
                <PinVerificationStep
                  emailAddress={emailAddress}
                  firstName={firstName}
                  loading={onboardingLoading}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  onPinCodeResend={onPinCodeResend}
                  values={values}
                />
              )}
            />
          </div>
        </SignupAlbumLayout.Wrapper>
      </div>
    </Fragment>
  );
};

export default MarketingAccountOnboardingView;
